import axios from 'axios';
import { Auth0ContextInterface } from '@auth0/auth0-react';
import config from '../../../config/config';
import { withSilentAccessToken } from '../../helper';
import { PERMISSIONS } from '../../constants';
import { SortOption } from '@lemontaps/lib/types/global';
import { isAxiosError } from '@/util';

export const getUnits = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  params?: SortOption & { page?: number; pageSize?: number; searchWord?: string },
) => {
  return await withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.get(config.API_BASE_URL + `theme/units`, {
        params: { ...(params || {}), pageSize: params?.pageSize || 9999 },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    [PERMISSIONS.READ.THEME_UNIT],
  );
};

export const deleteUnits = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  unitIds: Array<number>,
) => {
  return await withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.post(
        config.API_BASE_URL + `theme/units/delete`,
        {
          ids: unitIds,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      ),
    [PERMISSIONS.DELETE.THEME_UNIT],
  );
};

export const assignUnits = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  accountIds: readonly string[],
  mainUnitId: number | null,
  otherUnitIds: number[],
) => {
  try {
    return await withSilentAccessToken(
      getAccessTokenSilently,
      token =>
        axios.post(
          config.API_BASE_URL + `theme/units/assignAccounts`,
          {
            accounts: accountIds,
            mainUnitId,
            otherUnitIds,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        ),
      [PERMISSIONS.WRITE.THEME_PROFILES],
    );
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data?.error?.message || error.message);
    }
    throw new Error(error instanceof Error ? error.message : 'An unknown error occurred');
  }
};

//TODO: check if we need to restore the unassign
export const unassignUnits = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  accountIds: readonly string[],
  unitIds: number[],
) => {
  try {
    return await withSilentAccessToken(
      getAccessTokenSilently,
      token =>
        axios.post(
          config.API_BASE_URL + `theme/units/unAssignAccounts`,
          {
            accounts: accountIds,
            unitIds: unitIds,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        ),
      [PERMISSIONS.WRITE.THEME_PROFILES],
    );
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data?.error?.message || error.message);
    }
    throw new Error(error instanceof Error ? error.message : 'An unknown error occurred');
  }
};

export const createUnit = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  payload: { niceName: string },
) =>
  withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.post(config.API_BASE_URL + `theme/units`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    [PERMISSIONS.CREATE.THEME_UNIT],
  );

export const editUnit = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  payload: { niceName: string; id: number },
) =>
  withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.patch(config.API_BASE_URL + `theme/units/${payload.id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    [PERMISSIONS.WRITE.THEME_UNIT],
  );
